import { pure } from '@core/helpers/pure';
import {
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TuiBadge, TuiSkeleton } from '@taiga-ui/kit';
import { IsLoadingService, XAlertService } from '@core/services';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestRatingsService } from '@core/services/request/request-ratings.service';
import { RequestService } from '@core/services/request/request.service';
import { BehaviorSubject, EMPTY, of, Subject, switchMap } from 'rxjs';
import {
  LogisticRequestRatingActionEnum,
  LogisticRequestRatingList,
  RequestFormRatingsTranslations,
  RequestFormRatingsTranslationsPayload,
} from '@core/types';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError } from 'rxjs/operators';
import {
  TuiExpand,
  TuiButton,
  TuiCheckbox,
  TuiInput,
  TuiTextfield,
  TuiDialogService,
} from '@taiga-ui/core';
import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { TranslatePipe } from '@ngx-translate/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { EntityTranslatePipe } from '@core/pipes';

@Component({
  selector: 'x-request-form-ratings',
  imports: [
    TuiSkeleton,
    TuiTextfield,
    TuiCheckbox,
    FormsModule,
    TuiButton,
    TuiInput,
    NgClass,
    TuiExpand,
    NgTemplateOutlet,
    AsyncPipe,
    TranslatePipe,
    TuiBadge,
    NgStyle,
    EntityTranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './request-form-ratings.component.html',
})
export class RequestFormRatingsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(OutkeepPermissionService);
  private readonly requestService = inject(RequestService);
  private readonly requestRatingsService = inject(RequestRatingsService);
  private readonly dialogService = inject(TuiDialogService);
  public $ratingsToRender = this.requestRatingsService.$allRatings;
  public $ratingsStatus = this.requestRatingsService.$ratingsStatus;
  public $checkedRatings = this.requestRatingsService.$checkedRatings;
  private xAlertService = inject(XAlertService);
  private outkeepTranslateService = inject(OutkeepTranslateService);
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  private translates: RequestFormRatingsTranslationsPayload = {
    [RequestFormRatingsTranslations.ErrorUpdatingRatings]:
      'Случилась ошибка при обновлении рейтинга',
    [RequestFormRatingsTranslations.SuccessUpdatingRatings]:
      'Оценки проставлены! Перезагружаем изменённые заявки',
  };

  public readonly $isInitialLoad = signal(true);
  public readonly $canEditRequestRatings = signal(false);

  public toggleRating$: Subject<LogisticRequestRatingList> =
    new Subject<LogisticRequestRatingList>();

  public filterValue = '';

  $groupActionMode = input<boolean>(false, { alias: 'groupActionMode' });
  $groupActionsViewObserver = input<Subject<never> | null>(null, {
    alias: 'viewObserver',
  });
  $isInsideDealDetails = input<boolean>(false, {
    alias: 'isInsideDealDetails',
  });

  $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });

  public ratingsExpanded = false;

  ngOnInit(): void {
    this.initSubscriptions();
  }

  private initSubscriptions(): void {
    this.outkeepTranslateService
      .translate(Object.values(RequestFormRatingsTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
    this.requestRatingsService
      .setupRatings(this.$groupActionMode())
      .pipe(
        switchMap(() => {
          return this.toggleRating$.pipe(
            switchMap(rating =>
              this.requestRatingsService.toggleRating(
                rating.id,
                this.$ratingsStatus!()[rating.id]
                  ? LogisticRequestRatingActionEnum.ON
                  : LogisticRequestRatingActionEnum.OFF,
                this.$groupActionMode(),
              ),
            ),
            catchError(() => {
              this.xAlertService.error(
                this.translates[
                  RequestFormRatingsTranslations.ErrorUpdatingRatings
                ],
              );
              if (this.$groupActionMode()) {
                this.$groupActionsViewObserver()?.complete();
              } else {
                this.requestService.reloadRequest();
              }

              return EMPTY;
            }),
            switchMap(() => {
              if (this.$groupActionMode()) {
                this.xAlertService.success(
                  this.translates[
                    RequestFormRatingsTranslations.SuccessUpdatingRatings
                  ],
                );

                return this.requestService.reloadSelectedRequests$();
              }
              this.requestService.reloadRequest();

              return of(null);
            }),
            takeUntilDestroyed(this.destroyRef),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.$isInitialLoad()) {
          this.$isInitialLoad.set(false);
        }
      });

    this.permissionService.canEditRequestRatings$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(canEdit => {
        this.$canEditRequestRatings.set(canEdit);
      });
  }

  @pure
  public filteredRatings(filter: string, ratings: LogisticRequestRatingList[]) {
    if (!this.$canEditRequestRatings()) {
      const ratingsStatus = this.$ratingsStatus();

      return ratings.filter(rating => ratingsStatus[rating.id]);
    }

    if (!filter) {
      return ratings;
    }

    if (!this.ratingsExpanded) {
      this.ratingsExpanded = true;
    }

    return ratings.filter(rating =>
      rating.name.toLowerCase().includes(filter.toLowerCase()),
    );
  }

  public handleButtonClick(rating: LogisticRequestRatingList): void {
    this.$ratingsStatus.update(status => ({
      ...status,
      [rating.id]: !status[rating.id],
    }));
    this.toggleRating$.next(rating);
  }

  public openModal(): void {
    this.dialogService
      .open(new PolymorpheusComponent(RequestFormRatingsComponent), {
        closable: true,
        dismissible: true,
        size: null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
